import { showAlert } from "features/modal/modalSlice";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import colors from "assets/styles/color";
import { routes } from "routes";
import Available from "./components/Available";
import ChangePriceModal from "./components/ChangePriceModal";
import EstimatedAmount from "./components/EstimatedAmount";
import OrderProduct from "./components/OrderProduct";
import OverPriceModal from "./components/OverPriceModal";
import PickUpMethod from "./components/PickUpMethod";
import PurchaseHeader from "./components/PurchaseHeader";
import Reservation from "./components/Reservation";
import ShopCode from "./components/ShopCode";
import Terms from "./components/Terms";
import UserInfo from "./components/UserInfo";

import { Button, CustomModal } from "components";
import { selectBuyInfo, setBuyInfo } from "features/buy/buySlice";
import { hideLoading, showLoading } from "features/loading/loadingSlice";
import useUserInfoQuery from "hooks/queries/user";
import { useModal } from "hooks/useModal";
import { toNumber, validateInfo } from "utils/utils";

import { useGetPrePriceByZero } from "pages/goods-detail/components/DetailBottomSheet";
import { useGetGoodsDetail } from "pages/goods-detail/GoodsDetail";
import { useGetPrePrice } from "pages/goods-purchase/components/EstimatedAmount";
import { getSessionStorageItem } from "services/storageService";

// import { useTossPayment } from "hooks/useTossPayment"; // 결제 창 로드
import { getMallDepositDir } from "services/apis";
import { multiply, subtract } from "utils/mathematical";

function GoodsPurchase() {
  // const [bottomSheetState, setBottomSheetState] = useState("Close");

  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { PrePriceData } = useGetPrePrice();
  const { refetch } = useGetPrePriceByZero();
  const { data: userInfo } = useUserInfoQuery();
  const { detail, isLoading } = useGetGoodsDetail();
  const { BuyInfo, DeliveryInfo } = useSelector(selectBuyInfo);
  const { isTermsChecked } = BuyInfo;

  const [changePriceModalVisible, changePriceModalOpen, changePriceModalClose] =
    useModal(false); // 가격 변동 모달
  const [overPriceModalVisible, overPriceModalOpen, overPriceModalClose] =
    useModal(false); // 가격 초과 모달

  // const { TossProcess } = useTossPayment();

  // useEffect(() => {
  //   hideChoosePaymentMethod();
  // }, []);

  useEffect(() => {
    if (isLoading) {
      dispatch(showLoading());
    } else {
      dispatch(hideLoading());
    }
  }, [dispatch, detail, isLoading]);

  useEffect(() => {
    !isLoading &&
      dispatch(
        setBuyInfo({
          goodsId: id,
          assetType: detail.assetType,
          standardPrice: detail.standardPrice,
          earliestVisitDate: detail.earliestVisitDate,
        }),
      );
  }, [dispatch, detail, isLoading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSellingPriceChange = async () => {
    const { sellingPrice } = BuyInfo;
    const newPrePriceByZero = await refetch();
    if (newPrePriceByZero.data.price !== sellingPrice) {
      dispatch(
        setBuyInfo({
          sellingPrice: newPrePriceByZero.data.price,
          prevPrice: sellingPrice,
        }),
      );
    }
    return newPrePriceByZero;
  };

  // 매도잔량 부족
  const handleInsufficientStock = (TotalGoodsGram, useGram) => {
    const requiredGram = Number(subtract(TotalGoodsGram, useGram));

    if (PrePriceData?.balanceGrams < requiredGram) {
      dispatch(showAlert({ message: PrePriceData?.message + "\n재고 부족" }));
      return true;
    }
    return false;
  };

  const handlePurchaseRequired = () => {
    const { shopId, visitDate, isDelivery } = BuyInfo;
    const { receiver, postCode, address, addressDetail, phone } = DeliveryInfo;

    if (!isTermsChecked) {
      dispatch(
        showAlert({ icon: "Warning", message: "<약관/>을 <선택/>해주세요" }),
      );
    } else {
      const validation = validateInfo(
        isDelivery,
        isDelivery
          ? { receiver, postCode, address, addressDetail, phone }
          : { shopId, visitDate },
      );

      if (!validation.isValid) {
        dispatch(showAlert({ icon: "Warning", message: validation.message }));
      } else {
        return false;
      }
    }
    return true;
  };

  // 결제 방법에 따른 라우팅
  const DepositDirection = (type) => {
    switch (type) {
      // 헥토 간편 결제
      case "HECTO_SIMPLE_PAYMENT":
        return navigate(routes.settlement, {
          state: { id: id, goodsName: `${detail.goodsName}` },
        });
      // 헥토 계좌 등록
      case "HECTO_SIMPLE_REGISTER": {
        return navigate(routes.registerAccount, {
          state: { FromSettlement: true, id: id },
        });
      }
      // case "TOSS_QUICK_PAYMENT":
      // 임시 저장 후 토스 결제창 실행
      // TossProcess();
      // break;
      // case "HECTO_SIMPLE_DRIVE":
      // 간편결제로 이동
      //   if (BuyInfo?.depositPrice === 0) {
      //     return navigate(routes.settlement, { state: { id: id } });
      //   } else {
      //     return navigate(routes.registerAccount, {
      //       state: { FromSettlement: true, id: id },
      //     });
      //   }
      case "CS":
        // 한도 초과 모달 열기
        overPriceModalOpen();
        break;
      default:
        return;
    }
  };

  const onClickPurchase = async () => {
    const qty = getSessionStorageItem("qty");
    if (!qty) {
      return dispatch(
        showAlert({
          message: "1개 이상부터 구매하실 수 있습니다.",
          callback: () => {
            navigate(-1);
          },
        }),
      );
    }

    const TotalGoodsGram = Number(
      multiply(toNumber(qty), toNumber(detail?.goodsGram)),
    );
    const { useGram } = BuyInfo;
    // 보관료 체크

    const newPrePriceByZero = await handleSellingPriceChange();
    if (handleInsufficientStock(TotalGoodsGram, useGram)) return;
    if (handlePurchaseRequired()) return;

    const { sellingPrice, depositPrice } = BuyInfo;

    if (newPrePriceByZero.data.price !== sellingPrice) {
      // 가격변동 있으면
      changePriceModalOpen();
    } else {
      // 한도 관련해서 준 것 따라서
      if (depositPrice === 0) {
        navigate(routes.settlement, {
          state: { id: id, goodsName: `${detail.goodsName}` },
        });
      } else {
        // commit(52596e6adc3561e0706e995c38a209647581b79c) toss 내용 주석
        // getEnvConfig("TOSS_DEPOSIT_ONCE").then((res) => {
        //   console.log(res);
        //   if (Number(res.data.data.value) === 0) {
        //     navigate(routes.PurchaseUnabled);
        //   } else {
        getMallDepositDir(depositPrice).then((res) =>
          DepositDirection(res.data.data),
        );
        //   }
        // });
      }
    }
  };

  // 결제수단 선택 바텀시트 관리
  // const openChoosePaymentMethod = () => {
  //   setBottomSheetState("Open");
  // };

  // const closeChoosePaymentMethod = () => {
  //   setBottomSheetState("Close");
  // };

  // const hideChoosePaymentMethod = () => {
  //   setBottomSheetState("hidden");
  // };

  return (
    <>
      <PurchaseHeader title="주문/결제" />
      <CustomModal
        modalVisible={overPriceModalVisible}
        modalClose={overPriceModalClose}
      >
        <OverPriceModal modalClose={overPriceModalClose} />
      </CustomModal>
      <CustomModal
        modalVisible={changePriceModalVisible}
        modalClose={changePriceModalClose}
      >
        <ChangePriceModal
          modalClose={changePriceModalClose}
          prev={BuyInfo?.prevPrice}
          next={BuyInfo?.sellingPrice}
        />
      </CustomModal>
      {/* <ChoosePaymentMethod
        state={bottomSheetState}
        closeChoosePaymentMethod={closeChoosePaymentMethod}
        hideChoosePaymentMethod={hideChoosePaymentMethod}
        distanceFromTop="calc(100vh - 303px)"
      /> */}
      <Container>
        <ShopCode />
        <UserInfo
          name={userInfo?.name}
          phone={userInfo?.phone}
          email={userInfo?.email}
        />
        <PickUpMethod
          earliestVisitDate={detail?.earliestVisitDate}
          isPost={detail?.isPost}
          isShopTaken={detail?.isShopTaken}
        />
        <OrderProduct detail={detail} />
        <Available
          assetType={detail?.assetType}
          goodsGram={detail?.goodsGram}
        />
        {BuyInfo?.shopId !== 0 && <Reservation />}
        <EstimatedAmount />
        <Terms />
        <Button
          text="결제하기"
          size="md"
          variant="round"
          disabled={!isTermsChecked && "login"}
          addStyle={"margin-bottom: 64px"}
          onClickFn={onClickPurchase}
        />
      </Container>
    </>
  );
}

export default GoodsPurchase;

const Container = styled.div`
  width: 100%;
  height: auto;
  background-color: ${colors.purchaseBackground};

  padding: 16px;
`;
